import React, { useEffect, useState } from 'react';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { dateSlashFormant } from '../../shared/dateFunction';

const momentDate = moment();
const accountOpeningDate = moment('2015-01-01');

export default function Index(props) {
  const {
    initialData,
    reducer,
    title = 'Filter Orders',
    subtitle = 'Narrow down results by date range',
    onDateFilter,
    onClear,
  } = props;

  const [startDate, setStartDate] = useState(() => {
    if (initialData?.date?.from) {
      const m = moment(initialData.date.from, ['YYYY/MM/DD', 'YYYY-MM-DD']);
      return m.isValid() ? m : null;
    }
    return null;
  });

  const [endDate, setEndDate] = useState(() => {
    if (initialData?.date?.to) {
      const m = moment(initialData.date.to, ['YYYY/MM/DD', 'YYYY-MM-DD']);
      return m.isValid() ? m : null;
    }
    return null;
  });

  const [getDataStatus, setGetDataStatus] = useState(false);
  const [error, setError] = useState('');
  const dispatch = useDispatch();

  useEffect(() => {
    if (initialData?.date?.from) {
      const m = moment(initialData.date.from, ['YYYY/MM/DD', 'YYYY-MM-DD']);
      if (m.isValid()) setStartDate(m);
    }
    if (initialData?.date?.to) {
      const m = moment(initialData.date.to, ['YYYY/MM/DD', 'YYYY-MM-DD']);
      if (m.isValid()) setEndDate(m);
    }
  }, [initialData?.date?.from, initialData?.date?.to]);

  const handleDateChange = (newDate, name) => {
    setError('');
    setGetDataStatus(false);
    if (name === 'from') {
      setStartDate(newDate);
      if (endDate && moment(newDate).isAfter(endDate)) {
        setEndDate(newDate);
      }
    } else if (name === 'to') {
      setEndDate(newDate);
      if (startDate && moment(newDate).isBefore(startDate)) {
        setStartDate(newDate);
      }
    }
  };

  const fetchOrderData = (start, end) => {
    if (start && end && moment(start).isValid() && moment(end).isValid()) {
      const fromFormatted = dateSlashFormant(start);
      const toFormatted = dateSlashFormant(end);

      if (onDateFilter) {
        onDateFilter({ from: fromFormatted, to: toFormatted });
      } else if (reducer) {
        const requestData = {
          search: initialData?.search ?? null,
          filterBy: initialData?.filterBy ?? null,
          date: { from: fromFormatted, to: toFormatted },
          pagination: {
            sortBy: initialData?.pagination?.sortBy || 'id',
            descending: initialData?.pagination?.descending ?? true,
            page: 1,
            rowsPerPage: initialData?.pagination?.rowsPerPage || 10,
            rowsNumber: 0,
          },
          status: initialData?.status ?? 0,
        };

        dispatch(reducer(requestData));
      }
      setGetDataStatus(true);
    } else {
      setError('Please select a valid date range.');
    }
  };

  const fetchDefault = () => {
    setError('');
    const defaultStart = initialData?.date?.from
      ? moment(initialData.date.from, ['YYYY/MM/DD', 'YYYY-MM-DD'])
      : null;
    const defaultEnd = initialData?.date?.to
      ? moment(initialData.date.to, ['YYYY/MM/DD', 'YYYY-MM-DD'])
      : null;

    setStartDate(defaultStart?.isValid() ? defaultStart : null);
    setEndDate(defaultEnd?.isValid() ? defaultEnd : null);
    setGetDataStatus(false);

    if (onClear) {
      onClear();
    } else if (reducer) {
      dispatch(reducer(initialData));
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#FFF',
        border: '1px solid #EEF0F3',
        borderRadius: 3,
        overflow: 'hidden',
        mb: 2,
      }}
    >
      {/* ── Header strip ─────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: { xs: 2, sm: 2.5 },
          py: { xs: 1.5, sm: 1.75 },
          borderBottom: '1px solid #EEF0F3',
          backgroundColor: '#FBFCFE',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: 1.5,
            backgroundColor: 'rgba(0,34,85,0.06)',
            color: 'primary.main',
          }}
        >
          <CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>

        <Stack spacing={0.25} sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: { xs: '13px', sm: '14px' },
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: { xs: '10.5px', sm: '11.5px' },
              color: 'text.secondary',
              lineHeight: 1.2,
            }}
          >
            {subtitle}
          </Typography>
        </Stack>
      </Box>

      {/* ── Body: pickers + actions ───────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, md: 3 },
          px: { xs: 2, sm: 2.5 },
          py: { xs: 2, sm: 2.5 },
        }}
      >
        {/* Left: date range */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={{ xs: 1.5, sm: 1.5 }}
            >
              <DatePicker
                label="From Date"
                value={startDate}
                onChange={(newDate) => handleDateChange(newDate, 'from')}
                minDate={accountOpeningDate}
                maxDate={endDate || momentDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: { minWidth: { sm: 180 } },
                  },
                }}
              />

              <Divider
                orientation="vertical"
                flexItem
                sx={{ display: { xs: 'none', sm: 'block' }, my: 1 }}
              />

              <Typography
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  fontWeight: 500,
                  fontSize: '12px',
                  color: 'text.secondary',
                  px: 0.5,
                }}
              >
                to
              </Typography>

              <DatePicker
                label="To Date"
                value={endDate}
                onChange={(newDate) => handleDateChange(newDate, 'to')}
                minDate={startDate || accountOpeningDate}
                maxDate={momentDate}
                disabled={!startDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    sx: { minWidth: { sm: 180 } },
                  },
                }}
              />
            </Stack>
          </LocalizationProvider>

          {error && (
            <Typography
              sx={{
                fontSize: '11.5px',
                color: 'error.main',
                mt: 1,
                ml: 0.25,
              }}
            >
              {error}
            </Typography>
          )}
        </Box>

        {/* Right: actions */}
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={{ xs: 1, sm: 1.25 }}
          sx={{
            flexShrink: 0,
            width: { xs: '100%', md: 'auto' },
          }}
        >
          <Button
            variant="outlined"
            size="medium"
            startIcon={<FilterAltOffIcon sx={{ fontSize: 18 }} />}
            onClick={fetchDefault}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              py: 1,
              px: 2.25,
              borderRadius: 2,
              borderColor: '#D6DCE5',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'rgba(0,34,85,0.04)',
              },
            }}
          >
            Clear
          </Button>

          <Button
            variant="contained"
            size="medium"
            startIcon={<FilterAltIcon sx={{ fontSize: 18 }} />}
            disabled={getDataStatus}
            onClick={() => fetchOrderData(startDate, endDate)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '13px',
              py: 1,
              px: 2.5,
              borderRadius: 2,
              backgroundColor: '#002255',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#003366',
                boxShadow: 'none',
              },
            }}
          >
            Apply Filter
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
